import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';

import { DirectorySearchService } from '../../services/directory-search/directory-search.service';

import { DirectoryConfig, DirectoryField, DirectorySheet } from '../../models/directory-config.model';
import { FieldType, isLinkToItem } from '../../models/fields/field.model';
import { DirectoryEntityContactDialogComponent } from '../dialog/directory-entity-contact-dialog/directory-entity-contact-dialog.component';
import { CONSTANTS } from 'src/environments/constants';
import { Observable, of } from 'rxjs';
import { concatMap } from 'rxjs/operators';

@Component({
  selector: 'app-directory-entity',
  templateUrl: './directory-entity.component.html',
  styleUrls: ['./directory-entity.component.scss'],
})
export class DirectoryEntityComponent implements OnChanges {
  public FieldType = FieldType;
  public filteredFieldsGroups: any[] = [];
  public currentEntityData: any = "";
  public summary: any = {};
  public currentLang: string;
  public availableFields: any[];
  public mainIsEntity: boolean;
  public isLinkToItemModel = isLinkToItem;

  public sheetToUse: DirectorySheet;

  public static readonly ENTITY_KEYWORD: string = "entityForm";

  @Input() entityId: string;
  @Input() forceEntity: boolean = false;
  @Input() widgetConfig: DirectoryConfig;
  @Input() range: string;
  @Input() previewMode: boolean;
  @Input() isEditable: boolean;
  @Input() isSecondaryItem: boolean = false;

  constructor(
    private directorySearch: DirectorySearchService,
    public dialog: MatDialog,
    public translateService: TranslateService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    // console.debug("Widget config:");
    // console.debug(this.widgetConfig);
    // init Main or Not main to know if we can use the default name constant
    if (this.widgetConfig) {
      this.mainIsEntity = this.widgetConfig.isFocusOnAssociatedDB && this.isNotEmpty(this.widgetConfig.concernedAssociatedDB) ? false : true;
    }

    // Read the block displaying override & setting its value as a local property
    let showAllBlocks: boolean = false;
    if (this.widgetConfig?.layout?.showEmptyBlocks) {
      showAllBlocks = this.widgetConfig.layout.showEmptyBlocks;
    }

    if (this.range == undefined) {
    //  this.range = null;
      console.warn("WARNING: DirectoryEntityComponent's property range is undefined when it should either be null or an ADB class id");
    }

    // Select the proper sheet
    this.sheetToUse = this.findSheetToUse();

    // if(changes.entityId && changes.entityId.currentValue !== null && changes.entityId.currentValue !== changes.entityId.previousValue) {
      this.currentLang = this.translateService.currentLang;
      if ((this.range && this.range !== "") && !this.forceEntity) { // Element is expected to be an ADB element
        // console.debug("Element is expected to be an ADB element", this.range);
        // Getting the ADB's details so we can know whether it's an external one
        // TODO if deemed necessary by customer

        // Getting the element's details
        if (this.range && this.entityId) {
          this.directorySearch
            .getAdbElement(this.range, this.entityId) // Querying for the adb element fields' values
            .subscribe((entityData: any) => {
              this.currentEntityData = entityData;
              // console.debug("Element data");
              // console.debug(this.currentEntityData);

              // old widget this.mainIsEntity && !this.sheetToUse.classId

              if (this.mainIsEntity && !this.sheetToUse.classId) {
                this.mapFieldValue('title', {id:CONSTANTS.name, code: CONSTANTS.name, type:"Textarea"}); // No sheet available -> force using the name property as defined in the constants
                this.mapFieldValue('description', null);
                this.mapFieldValue('logo', null);
                this.mapFieldValue('banner', null);
              } else {
                this.mapFieldValue('title', this.sheetToUse.title);
                this.mapFieldValue('description', this.sheetToUse.description);
                this.mapFieldValue('logo', this.sheetToUse.logo);
                this.mapFieldValue('banner', this.sheetToUse.banner);
              }

              // this method is now called here so we're sure we have access to the entity values
              this.filterFieldsThenGroups(this.sheetToUse.fieldsgroup, this.range, showAllBlocks);

            });
        }
      } else { // Element is expected to be an entity
        // console.debug("Element is expected to be an entity", this.range);
        this.directorySearch
          .findEntityData(this.entityId, true) // Querying for the entity fields' values
          .subscribe((entityData: any) => {
            // console.debug("Displaying entity: " + this.entityId);
            // console.debug(entityData);
            this.currentEntityData = entityData;
            // console.debug("Entity data", this.currentEntityData);

            // old widget !this.mainIsEntity && !this.sheetToUse.classId

            if (!this.mainIsEntity && !this.sheetToUse.classId ) {
              this.mapFieldValue('title', {id:CONSTANTS.mainName, code: CONSTANTS.mainName, type:"Textarea"}); // No sheet aviable force using the name property as defined in the constants
              this.mapFieldValue('description', null);
              this.mapFieldValue('logo', null);
              this.mapFieldValue('banner', null);
            } else {
              this.mapFieldValue('title', this.sheetToUse.title);
              this.mapFieldValue('description', this.sheetToUse.description);
              this.mapFieldValue('logo', this.sheetToUse.logo);
              this.mapFieldValue('banner', this.sheetToUse.banner);
            }

            // this method is now called here so we're sure we have access to the element values
            this.filterFieldsThenGroups(this.sheetToUse.fieldsgroup, this.range, showAllBlocks);

          });
      }
  }

  private findSheetToUse(): DirectorySheet {
   // console.debug("Finding which sheet to use",this.isSecondaryItem, this.range, this.forceEntity);
    if (this.isSecondaryItem && this.widgetConfig.layout.onlySpecificFields) { // Secondary items may have a specific sheet defined
      let sheetKeyToSearch: string;
      if ((this.range && this.range !== "") && !this.forceEntity) { // item is an adb element, key is the adb id
        sheetKeyToSearch = this.range;
      } else { // item is an entity, this uses a specific key
        sheetKeyToSearch = DirectoryEntityComponent.ENTITY_KEYWORD;
      }

      // console.debug("Looking for secondary sheet with key: " + sheetKeyToSearch);
      const existingSheet = this.findSecondarySheetInConfig(this.widgetConfig, sheetKeyToSearch);
      if (existingSheet !== false) {
        // console.debug("Found secondary sheet:");
        // console.debug(existingSheet);
        return existingSheet;
      }
    }

    // console.debug("MAIN OBJECT SHEET", this.widgetConfig.sheet);
    return this.widgetConfig.sheet;
  }

  private findSecondarySheetInConfig(config: DirectoryConfig, adbId: string): DirectorySheet|false {
    if (config.secondarySheets) {
      const toReturn = config.secondarySheets.find(adb => {return adb.classId == adbId;});
      if (toReturn != undefined) return toReturn;
    }
    return false;
  }

  public isNotEmpty (concernedAssociatedDB: string) {
    return concernedAssociatedDB != undefined && concernedAssociatedDB != null && concernedAssociatedDB != "";
  }

  private mapFieldValue(property: string, field: DirectoryField) {
    if (field && field.id) {
      this.summary[property] = {
        field: field,
        value: field.id in this.currentEntityData ? this.currentEntityData[field.id] : null
      };
    } else {
      this.summary[property] = null;
    }
  }

  /**
   * Fills the list of groups & their lists of fields
   * 
   * @param fieldsGroups 
   * @param range 
   * @param showAllBlocks 
   * @returns 
   */
  public filterFieldsThenGroups(fieldsGroups, range: string, showAllBlocks: boolean) {
    //this.showFields = false;
    this.filteredFieldsGroups = [];
    // console.log("Current entity data", this.currentEntityData);
    if (range == '') range = null;

    fieldsGroups.forEach((group) => {
      let newGroup = {order: group.order, title: group.title, fields: []};

      // If settings ask to display empty blocks, add them here
      if (showAllBlocks) {
        // console.log("All blocks will be shown");
        this.filteredFieldsGroups.push(newGroup);
      }

      // console.log("Current entity", this.currentEntityData);
      // console.log("Current sheet", this.sheetToUse);
      group.fields.forEach((field) => {
        let fieldDomain = field.domain;
        if (fieldDomain == undefined) { // Entity fields don't even have a "domain" property, having us compare undefined -v- null. This aims at fixing it in a rather unelegant way.
          fieldDomain = null;
        }

        if ((fieldDomain === range && field.id != 'core_hasListName') //Entities and ADB without 'core_hasListName'
            || (range != null && field.id == 'core_hasListName')) { //Specific to ADB with 'core_hasListName'

          // console.log("Trying to push field", field);
          // Add the field to the group IF
          if (this.fieldCanExistInEntity(field.interestArea, field.communities) // the field's interest area is among the element's ones
              || (field.interestArea === undefined)) { // field is a core field
            if ( field.showFieldWhenNoValue // IF the field is supposed to be shown even without a value
                || !this.previewMode // OR if this is not a preview mode (ie we're editing)
              ) { // THEN always show field
                newGroup.fields.push(field);
                // console.debug("Field " + field.id + " can be displayed even without a value");
                // If the group wasn't already added to the list of groups, then add it now
                if (this.filteredFieldsGroups.indexOf(newGroup) == -1) {
                  // console.log("Pushing group ");
                  // console.log(newGroup.title);
                  this.filteredFieldsGroups.push(newGroup);
                } 
              } else if(this.currentEntityData.hasOwnProperty(field.id)) { // OTHERWISE IF the element to display has a value defined for this field
              // #456 now would be a good time to make sure the list field values are authorized with regards to the widget's configuration
                this.fieldReallyHasValue(field).subscribe(reallyHasValue => {
                  if (reallyHasValue) {
                    // console.debug("Field "+field.id+" can be displayed because is has a value");
                    // console.debug("Field", field);
                    newGroup.fields.push(field);
                    // If the group wasn't already added to the list of groups, then add it now
                    if (this.filteredFieldsGroups.indexOf(newGroup) == -1) {
                      // console.log("Pushing group ");
                      // console.log(newGroup.title);
                      this.filteredFieldsGroups.push(newGroup);
                    } 
                  } else {
                    // console.debug("Field should not be displayed", field);
                  }
                });
              }
          }

        }
      });
    });
    // console.debug("Fields AFTER filtering");
    // console.debug(this.filteredFieldsGroups);
    return this.filteredFieldsGroups;
  }

  private fieldReallyHasValue(field: any): Observable<boolean> {
    // console.debug("Starting fieldReallyHasValue", field);

    if (!this.isLinkToItemModel(field.type)) { // has a value and is not a link to a potentially restricted list of items
      // console.debug("Safe field");
      return of(true);
    }

    const currentValue = this.currentEntityData[field.id];
    // console.debug("Field value", currentValue);
    const fieldRange = field.range;
    return this.directorySearch.getAllowedItems(field.range).pipe(
      concatMap(allowedIds => {
        // console.debug("Allowed ids for "+ field.range, allowedIds);
        if (Array.isArray(currentValue)) { // multiple values to test
          for (let index = 0; index < currentValue.length; index++) {
            const element = currentValue[index];
            if (allowedIds.includes(element.element_id)) {
              return of(true);
            }
          }
          return of(false);
        } else {
          if (field.range == null || field.range == undefined) { // specific way the unique entities values are stored
            return of(allowedIds.includes(currentValue));
          } else {
            return of(allowedIds.includes(currentValue.element_id));
          }
        }
      })
    );
  }

  fieldCanExistInEntity(fieldInterestAreaID: string, fieldCommunities: string[]): boolean {
    // console.log("Comparing", this.currentEntityData.interest_areas);
    // console.log("with", fieldInterestAreaID);
    // If the entity is an ADB element there's no need to test the interest area compatibility
    if (this.currentEntityData.hasOwnProperty("element_id")) {
      return true;
    }

    // Making sure the field's interest area is found in the entity's list of interest areas
    const matchingInterestArea = this.currentEntityData.interest_areas.find((interestArea) => {
      return interestArea.interest_area_id == fieldInterestAreaID;
    });

    if (matchingInterestArea != undefined || fieldInterestAreaID == CONSTANTS.commonIA) {
      // return true;
      // we must also check that the communities for field & entity intersect
      const communitiesIntersection: any[] = this.currentEntityData.community.filter(x => fieldCommunities.includes(x.community_id));
      // console.log("Communities intersection", communitiesIntersection);
      return communitiesIntersection.length > 0;
    }

    return false;
  }

  canContact() {
    if (this.widgetConfig && this.widgetConfig.requests.hasContactRequests
        && this.widgetConfig.requests.contact.code && this.currentEntityData
          && this.currentEntityData[this.widgetConfig.requests.contact.code]) {
      return true;
    }
    return false;
  }

  public openContactDialog(contact?: DirectoryField) {
    let contactCode = this.widgetConfig.requests.contact.code;
    let contactEmail;
    if (this.currentEntityData[contactCode]) {
      contactEmail = this.currentEntityData[contactCode];
    } else {
      contactEmail = this.widgetConfig.responsibleEmail;
    }
    if (contactEmail) {
      const dialogRef = this.dialog.open(DirectoryEntityContactDialogComponent, {
        width: '600px',
        maxWidth: '100vw',
        maxHeight: '90vh',
        data: {
          entity_id: this.entityId,
          entity_name: this.currentEntityData[this.sheetToUse.title.id],
          widget_name: this.widgetConfig.name,
          referer: document.referrer,
          contact_email: contactEmail
        },
        autoFocus: false,
        disableClose: true,
      });
      dialogRef.afterClosed().subscribe((success: string) => {});
    }
  }

  public translationCompatibilityForOlderWidget(objectOrString, lang): {} {
    if (typeof objectOrString !== 'object') {
      let tempValue = objectOrString
      objectOrString = {};
      objectOrString[lang] = tempValue;
    }
    return objectOrString[lang];
  }

  // can't use simple quote than encodeURI for backgroundImage url
  public urlEnc(url: string) {
    return encodeURI(url);
  }

  // public getCurrentValue(field) {
  //   if (this.currentEntityData[field.id] instanceof Object) {
  //       return this.currentEntityData[field.id].element_id;
  //   } else {
  //     return this.currentEntityData[field.id];
  //   }
  // }

}
